import { ApiError, NetworkError, TimeoutError } from '../errors/ApiError';

/**
 * Configuration for API client
 */
const DEFAULT_CONFIG = {
  timeout: 10000, // 10 seconds
  retries: 2,
  retryDelay: 1000, // 1 second
  baseURL: '',
};

/**
 * Enhanced API Client with better error handling, retries, and configuration
 */
export class ApiClient {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.requestQueue = new Map(); // For request deduplication
  }

  /**
   * Make an authenticated API call with enhanced error handling
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Fetch options
   * @param {string} token - Authentication token
   * @returns {Promise<any>} API response data
   */
  async call(endpoint, options = {}, token = null) {
    if (!token) {
      throw new ApiError('No authentication token available', 401, 'Unauthorized');
    }

    const requestKey = this._getRequestKey(endpoint, options);
    
    // Check for duplicate requests
    if (this.requestQueue.has(requestKey)) {
      return this.requestQueue.get(requestKey);
    }

    const requestPromise = this._makeRequest(endpoint, options, token);
    this.requestQueue.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.requestQueue.delete(requestKey);
    }
  }

  /**
   * Make the actual HTTP request with retry logic
   * @private
   */
  async _makeRequest(endpoint, options, token, attempt = 1) {
    const url = `${this.config.baseURL}${endpoint}`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new ApiError(
          `API call failed: ${response.status} ${response.statusText} - ${errorText}`,
          response.status,
          response.statusText,
          errorText
        );
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      }
      
      return null; // For 204 No Content responses
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${this.config.timeout}ms`);
      }

      if (error instanceof ApiError) {
        throw error;
      }

      // Network or other errors
      if (attempt < this.config.retries && this._shouldRetry(error)) {
        await this._delay(this.config.retryDelay * attempt);
        return this._makeRequest(endpoint, options, token, attempt + 1);
      }

      throw new NetworkError(`Network error: ${error.message}`);
    }
  }

  /**
   * Determine if a request should be retried
   * @private
   */
  _shouldRetry(error) {
    // Retry on network errors or 5xx server errors
    return error.name === 'TypeError' || 
           (error.status && error.status >= 500);
  }

  /**
   * Delay execution for retry logic
   * @private
   */
  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generate a unique key for request deduplication
   * @private
   */
  _getRequestKey(endpoint, options) {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${endpoint}:${body}`;
  }

  /**
   * Update base URL configuration
   */
  setBaseURL(baseURL) {
    this.config.baseURL = baseURL;
  }

  /**
   * Update timeout configuration
   */
  setTimeout(timeout) {
    this.config.timeout = timeout;
  }
}
